import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, FileDown, RefreshCw } from 'lucide-react';
import EngineReportV1 from '@/components/mfcwl-report/EngineReportV1';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/mfcwl_logo.png';

const stripSpaces = (s) => (s || '').replace(/\s+/g, '').toUpperCase();
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN',
  { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function MfcwlPublicReportPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!id || !token) {
      setError('invalid-link');
      setLoading(false);
      return;
    }
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/b2b/mfcwl/public/inspections/${id}/report.json?token=${token}`);
      setDoc(r.data);
      setError(null);
    } catch (e) {
      if (!silent) setError(e.response?.data?.detail || 'fetch-failed');
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s while the analysis is still running (long AI run).
  const pending = doc && !['COMPLETED', 'FAILED'].includes(doc.analysis_status);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [pending, load]);

  const downloadPdf = () => {
    window.open(`${API_URL}/b2b/mfcwl/public/inspections/${id}/report.pdf?token=${token}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-gray-500">
        <div className="text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          <div className="mt-2 text-sm">Loading report…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="mt-3 text-lg font-semibold text-gray-800">Report unavailable</h2>
          <p className="mt-1 text-sm text-gray-600">
            {error === 'invalid-or-expired-token'
              ? 'This link has expired or is invalid. Please request a fresh link.'
              : error === 'inspection-not-found'
                ? 'We could not find this inspection.'
                : 'We could not load this report. Please try again later.'}
          </p>
        </div>
      </div>
    );
  }

  const insp = doc?.inspection || {};
  const v = insp.vehicle || {};
  const c = insp.customer || {};
  const loc = insp.location || {};
  const analysis = doc?.analysis || null;
  // `analysis.result` is the schema-v1 engine-sound AI payload.
  const result = analysis?.result || null;
  const ready = doc?.analysis_status === 'COMPLETED' && !!result;
  const failed = doc?.analysis_status === 'FAILED';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mfcwl-public-report">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Mahindra First Choice" className="h-10 object-contain" />
            <div className="border-l border-gray-200 pl-4">
              <div className="text-xs text-gray-500 uppercase tracking-wider">Vehicle Inspection Report</div>
              <div className="text-sm font-semibold text-gray-900 font-mono">{insp.inspection_id}</div>
            </div>
          </div>
          <button
            onClick={downloadPdf}
            disabled={!ready}
            className="inline-flex items-center gap-2 bg-[#B90024] hover:bg-[#A1001F] disabled:opacity-40 text-white text-sm font-medium px-4 py-2 rounded-md transition"
            data-testid="public-download-pdf"
          >
            <FileDown className="h-4 w-4" /> Download PDF
          </button>
        </div>
        <div className="h-[3px] bg-[#B90024]" />
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Summary strip — inspection + vehicle */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-3">Inspection</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-gray-500">Scheduled</dt><dd className="text-gray-800">{fmtDate(insp.scheduled_at)}</dd>
              <dt className="text-gray-500">Status</dt><dd className="text-gray-800">{insp.status || '—'}</dd>
              <dt className="text-gray-500">Customer</dt><dd className="text-gray-800">{c.name || '—'}</dd>
              <dt className="text-gray-500">Contact</dt><dd className="text-gray-800">{c.phone || '—'}</dd>
              <dt className="text-gray-500">Location</dt>
              <dd className="text-gray-800">
                {[loc.city, loc.pincode].filter(Boolean).join(' · ') || '—'}
              </dd>
            </dl>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-xs uppercase tracking-wider text-gray-500 mb-3">Vehicle</h2>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <dt className="text-gray-500">Reg No</dt><dd className="text-gray-900 font-mono font-semibold">{stripSpaces(v.reg_no) || '—'}</dd>
              <dt className="text-gray-500">Make · Model</dt><dd className="text-gray-800">{[v.make, v.model].filter(Boolean).join(' ') || '—'}</dd>
              <dt className="text-gray-500">Year</dt><dd className="text-gray-800">{v.year || '—'}</dd>
              <dt className="text-gray-500">Fuel</dt><dd className="text-gray-800">{v.fuel_type || '—'}</dd>
              <dt className="text-gray-500">Transmission</dt><dd className="text-gray-800">{v.transmission || '—'}</dd>
              <dt className="text-gray-500">Kms driven</dt><dd className="text-gray-800">{(v.km_driven || 0).toLocaleString('en-IN')} km</dd>
              <dt className="text-gray-500">Turbo</dt><dd className="text-gray-800">{v.has_turbo ? 'Yes' : 'No'}</dd>
            </dl>
          </div>
        </section>

        {/* Schema-v1 engine-sound AI report (MFCWL-branded) */}
        {ready
          ? <EngineReportV1 result={result} />
          : failed
          ? (
            <section className="bg-white rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
              <AlertTriangle className="h-6 w-6 text-rose-500 mx-auto mb-2" />
              <div className="text-sm text-rose-900">
                The engine sound analysis could not be completed for this inspection.
              </div>
            </section>
          )
          : (
            <section className="bg-white rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-amber-600 mx-auto mb-2" />
              <div className="text-sm text-amber-900">
                Analysis in progress — current stage: <b>{doc?.analysis_status || 'PENDING'}</b>.
                {refreshing && <span className="ml-1 text-amber-700">refreshing…</span>}
              </div>
              <p className="text-[11px] text-amber-700 mt-1">
                Engine sound analysis typically takes 6–10 minutes. This page updates automatically.
              </p>
              <button onClick={() => load(true)}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 border border-amber-300 rounded-md px-3 py-1.5 hover:bg-amber-100 transition"
                      data-testid="public-report-refresh">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh now
              </button>
            </section>
          )}

        <footer className="pt-6 border-t border-gray-200 text-[11px] text-gray-400 leading-relaxed">
          This report is generated from a combination of on-board diagnostic (OBD) readings,
          mechanic-captured engine sound, and AI-driven component analysis. Recommendations
          are advisory and should be validated by a qualified technician before commercial decisions.
          Generated {fmtDate(doc?.generated_at)}.
        </footer>
      </main>
    </div>
  );
}
